/**
 * @hyggebam/shared — the contract shared by server and web.
 *
 * Mirrors the spec's data model (§9) and per-title states (§3). The matcher,
 * import pipeline, availability cache, and API all speak these types.
 */

// ── Per-person, per-title state (§3) ────────────────────────────────────────
// unseen   — not yet shown to this person
// like     — swipe right ("yes")
// later    — swipe up ("not now"); soft no, may resurface
// never    — swipe left ("never"); hard exclude, permanent
// seen_rated — imported from IMDb ratings (already watched + graded signal)
// watched  — couple watched this match; may carry a post-watch rating
// abandoned — started but didn't finish; mild negative, not re-offered as match
export const TITLE_STATES = [
  "unseen",
  "like",
  "later",
  "never",
  "seen_rated",
  "watched",
  "abandoned",
] as const;
export type TitleStateValue = (typeof TITLE_STATES)[number];

// States that hard-exclude a title from ever matching for a person.
export const HARD_EXCLUDE_STATES: TitleStateValue[] = ["never"];
// States meaning "already dealt with" — not a fresh candidate.
export const CONSUMED_STATES: TitleStateValue[] = ["watched", "abandoned", "seen_rated"];

// ── Derived couple state, per-title (§3) ────────────────────────────────────
export type CoupleTitleState = "match" | "dead" | "pending";

// ── Match lifecycle (§9) ────────────────────────────────────────────────────
export const MATCH_STATUSES = [
  "buffered", // in the rolling buffer, not yet shown on Matches screen
  "surfaced", // shown to the couple on the Matches screen
  "chosen", // couple picked it to watch
  "awaiting_feedback", // chosen + waiting for the "did you watch it?" prompt
  "watched", // both rated / confirmed watched
  "abandoned", // started but didn't finish
  "expired", // fell out of availability before being watched
] as const;
export type MatchStatus = (typeof MATCH_STATUSES)[number];

// ── Availability offer types (§5). v1 only consumes `flatrate`. ──────────────
export const OFFER_TYPES = ["flatrate", "rent", "buy", "free", "ads"] as const;
export type OfferType = (typeof OFFER_TYPES)[number];
export const V1_OFFER_TYPE: OfferType = "flatrate";

// ── Swipe gestures ───────────────────────────────────────────────────────────
// up    = "I don't want to see it"  → never (hard exclude, permanent)
// left  = "not right now"           → later (soft no, may resurface)
// right = "yes"                     → like
// down  = "seen it"                 → watched (already seen; excluded from deck)
// This mapping is identical for ALL swiping (main deck, wishlist battle, …).
export type SwipeDirection = "left" | "up" | "right" | "down";
export const SWIPE_TO_STATE: Record<SwipeDirection, TitleStateValue> = {
  up: "never",
  left: "later",
  right: "like",
  down: "watched",
};

// ── Import sources (§4) ─────────────────────────────────────────────────────
export const IMPORT_SOURCES = [
  "imdb_ratings",
  "imdb_watchlist",
  "letterboxd",
  "trakt",
] as const;
export type ImportSource = (typeof IMPORT_SOURCES)[number];

export const IMPORT_STATUSES = ["queued", "running", "done", "failed"] as const;
export type ImportStatus = (typeof IMPORT_STATUSES)[number];

// ── Entities (§9) ────────────────────────────────────────────────────────────

export interface Couple {
  id: string;
  secret_token: string;
  created_at: number;
  current_country: string; // ISO-3166-1 alpha-2, e.g. "DK"
  services: string[]; // TMDB provider ids (as strings) the couple subscribes to
  last_active_at: number;
  theme_id: string | null; // couple-wide theme (a partner can "set for couple")
  preferred_language: string; // ISO 639-1 code; movie overviews are translated to this on the fly
}

/** Languages the overview can be translated into (Cloudflare m2m100 codes).
 *  "en" is the source/no-op. Keep this list and the picker in sync. */
export const SUPPORTED_LANGUAGES: { code: string; label: string }[] = [
  { code: "en", label: "English" },
  { code: "da", label: "Dansk" },
  { code: "sv", label: "Svenska" },
  { code: "de", label: "Deutsch" },
  { code: "fr", label: "Français" },
  { code: "es", label: "Español" },
  { code: "it", label: "Italiano" },
  { code: "pt", label: "Português" },
  { code: "nl", label: "Nederlands" },
  { code: "pl", label: "Polski" },
  { code: "ko", label: "한국어" },
  { code: "ja", label: "日本語" },
  { code: "zh", label: "中文" },
];
export const SUPPORTED_LANGUAGE_CODES = SUPPORTED_LANGUAGES.map((l) => l.code);

export interface Person {
  id: string;
  couple_id: string;
  display_name: string;
}

export interface Title {
  tmdb_id: number;
  imdb_id: string | null;
  title: string;
  year: number | null;
  genres: string[];
  cast: string[];
  runtime: number | null;
  poster: string | null;
  popularity: number;
  overview: string | null;
  tmdb_score: number | null; // TMDB rating, 0–100 (vote_average × 10); null if unknown
}

export interface TitleState {
  person_id: string;
  tmdb_id: number;
  state: TitleStateValue;
  signal: number; // [-1, 1] strength of preference signal
  rating: number | null; // post-watch / imported rating, 1-10 scale
  updated_at: number;
}

export interface Match {
  couple_id: string;
  tmdb_id: number;
  created_at: number;
  status: MatchStatus;
  joint_score: number;
}

export interface Availability {
  tmdb_id: number;
  country: string;
  service: string; // TMDB provider id
  offer_type: OfferType;
  updated_at: number;
}

export interface ImportJob {
  id: string;
  person_id: string;
  source: ImportSource;
  status: ImportStatus;
  stats: ImportJobStats;
  created_at: number;
}

export interface ImportJobStats {
  rows_total: number;
  rows_parsed: number;
  rows_matched: number;
  rows_failed: number;
  error?: string;
}

// ── Streaming provider (for the services picker UI) ─────────────────────────
export interface Provider {
  provider_id: string;
  name: string;
  logo_path: string | null;
}

// ── API payload shapes (what the web SPA consumes) ──────────────────────────

export interface SeatView {
  person_id: string;
  display_name: string;
}

export interface CoupleView {
  id: string;
  share_url: string;
  short_code: string;
  current_country: string;
  services: string[];
  seats: SeatView[];
  created_at: number;
  theme_id: string | null; // couple-wide theme, if one is set
  preferred_language: string; // ISO 639-1 code overviews are translated into
}

export interface DeckCard {
  tmdb_id: number;
  title: string;
  year: number | null;
  genres: string[];
  cast: string[];
  runtime: number | null;
  poster: string | null;
  overview: string | null;
  predicted_pref: number; // this person's predicted preference [0,1]
  available_on: ProviderOffer[]; // flatrate offers in the couple's country
  ratings: RatingValue[]; // external ratings (IMDb/RT/Metacritic/TMDB); may be partial or empty
}

export interface ProviderOffer {
  service: string;
  name: string;
  logo_path: string | null;
}

export interface MatchView {
  tmdb_id: number;
  title: string;
  year: number | null;
  poster: string | null;
  genres: string[];
  runtime: number | null;
  joint_score: number;
  status: MatchStatus;
  available_on: ProviderOffer[];
}

export interface MatchesScreen {
  ready: boolean; // buffer has ≥3 available matches
  matches: MatchView[];
  attribution: string; // JustWatch attribution string (§5)
}

// ── Presence (async): "your partner is swiping too" ──────────────────────────
// The app is fully async; presence is only a lightweight indicator that the
// other seat is on the deck right now. There is no separate co-swipe mode.
export interface LivePresence {
  person_id: string;
  display_name: string;
  online: boolean; // last_seen_at within the presence window
}
export interface PresenceView {
  presence: LivePresence[];
}

export interface PendingFeedback {
  tmdb_id: number;
  title: string;
  poster: string | null;
}

// The JustWatch attribution required whenever availability is shown (§5, §10).
export const JUSTWATCH_ATTRIBUTION =
  "Streaming availability data provided by JustWatch.";

// ── Wishlist (shared couple list) + search ──────────────────────────────────
export interface WishlistEntry {
  tmdb_id: number;
  title: string;
  year: number | null;
  poster: string | null;
  genres: string[];
  added_by: string; // person_id
  available_on: ProviderOffer[];
}

export interface SearchResult {
  tmdb_id: number;
  title: string;
  year: number | null;
  poster: string | null;
  genres: string[];
  in_wishlist: boolean;
}

// ── Seen list (titles this person marked watched/seen) ──────────────────────
export interface SeenEntry {
  tmdb_id: number;
  title: string;
  year: number | null;
  poster: string | null;
  genres: string[];
  rating: number | null; // this person's rating, if they gave one
}

/** Where a swipe deck draws candidates from.
 *  - "all"         — the normal deck of titles fresh for the couple (nobody's seen)
 *  - "wishlist"    — battle mode: the shared wishlist only
 *  - "recommended" — a non-dominant rail of titles a partner has SEEN & LOVED
 *                    (rating ≥ LOVED_RATING) that the requester hasn't seen */
export type DeckSource = "all" | "wishlist" | "recommended";

// ── "Where you align" — overlap of titles BOTH partners have already seen ────
export interface AlignmentRating {
  person_id: string;
  display_name: string;
  rating: number | null; // this person's 1–10 rating, if they gave one
}
export interface AlignmentEntry {
  tmdb_id: number;
  title: string;
  year: number | null;
  poster: string | null;
  genres: string[];
  ratings: AlignmentRating[]; // one per person, same order as the couple's seats
}
export interface AlignmentView {
  both_loved: AlignmentEntry[]; // every partner rated ≥ LOVED_RATING
  both_hated: AlignmentEntry[]; // every partner rated ≤ HATED_RATING
  split: AlignmentEntry[]; // someone loved it, someone hated it
}

// ── Seen-rating tiers (1–10). Shared so server + web agree on the bands. ─────
export const LOVED_RATING = 7; // ≥ this = "loved it"
export const HATED_RATING = 4; // ≤ this = "hated it"
export const DESPISED_RATING = 2; // ≤ this = "it's shit" (treated like a backward `never`)

// ── Movie details (details page) ────────────────────────────────────────────
export type RatingSource = "TMDB" | "IMDb" | "Rotten Tomatoes" | "Metacritic";

export interface RatingValue {
  source: RatingSource;
  display: string; // e.g. "8.3", "92%"
  score: number | null; // normalised 0–100 for sorting/bars
}

export interface PersonState {
  person_id: string;
  display_name: string;
  state: TitleStateValue;
  rating: number | null;
}

export interface TitleDetails {
  tmdb_id: number;
  title: string;
  year: number | null;
  runtime: number | null;
  genres: string[];
  cast: string[];
  director: string | null;
  overview: string | null;
  poster: string | null;
  backdrop: string | null;
  ratings: RatingValue[]; // multi-source, most important first
  trailer_url: string | null; // YouTube watch URL
  available_on: ProviderOffer[];
  in_wishlist: boolean;
  states: PersonState[]; // each partner's state for this title
}

// ── Theming (AI-generated, runtime design tokens) ───────────────────────────
// HARD CONSTRAINT: every themeable surface in the UI reads one of these tokens
// (never a literal), so a Theme can re-skin the whole app at runtime.
export interface ThemePalette {
  bg: string; // page background base
  bg2: string; // raised background / inputs
  card: string; // cards / surfaces
  ink: string; // primary text
  muted: string; // secondary text
  accent: string; // brand / primary action
  accent2: string; // secondary brand (gradients)
  like: string; // right-swipe / positive
  never: string; // up-swipe / negative
  later: string; // left-swipe / neutral-wait
}

export interface Theme {
  id: string;
  name: string; // short label, e.g. "Snoopy"
  prompt: string; // the user's request that produced it
  palette: ThemePalette;
  radius: number; // card corner radius, px
  font: string | null; // Google Font family, or null = system default
  images: {
    background: string | null; // full-bleed app background (R2 URL)
    accent: string | null; // decorative accent / illustration (R2 URL)
  };
}

export const DEFAULT_THEME: Theme = {
  id: "default",
  name: "Hygge",
  prompt: "",
  palette: {
    bg: "#13111c",
    bg2: "#1c1830",
    card: "#241f3a",
    ink: "#f3eefe",
    muted: "#a79fc4",
    accent: "#ff6b9d",
    accent2: "#7c5cff",
    like: "#3ddc97",
    never: "#ff5d6c",
    later: "#ffc857",
  },
  radius: 22,
  font: null,
  images: { background: null, accent: null },
};

/** Five curated, deliberately-different starter themes for onboarding. */
export const PRESET_THEMES: Theme[] = [
  DEFAULT_THEME, // "Hygge" — cosy dark violet
  {
    id: "preset-sunset",
    name: "Sunset",
    prompt: "",
    palette: { bg: "#1b0f14", bg2: "#271319", card: "#33191f", ink: "#fff3ec", muted: "#d4a394", accent: "#ff8c42", accent2: "#ff3d77", like: "#ffd166", never: "#ff5d6c", later: "#ffb38a" },
    radius: 20,
    font: "Poppins",
    images: { background: null, accent: null },
  },
  {
    id: "preset-forest",
    name: "Forest",
    prompt: "",
    palette: { bg: "#0d1812", bg2: "#13241a", card: "#1b3225", ink: "#eafff0", muted: "#9fc7ad", accent: "#4ade80", accent2: "#f5c451", like: "#4ade80", never: "#ff6b6b", later: "#f5c451" },
    radius: 16,
    font: "Nunito",
    images: { background: null, accent: null },
  },
  {
    id: "preset-noir",
    name: "Noir",
    prompt: "",
    palette: { bg: "#0a0a0a", bg2: "#161616", card: "#1d1d1d", ink: "#ffffff", muted: "#9a9a9a", accent: "#e6e6e6", accent2: "#9a9a9a", like: "#34c759", never: "#ff453a", later: "#ffd60a" },
    radius: 6,
    font: "Space Mono",
    images: { background: null, accent: null },
  },
  {
    id: "preset-bubblegum",
    name: "Bubblegum",
    prompt: "",
    palette: { bg: "#fff0f6", bg2: "#ffe3ef", card: "#ffffff", ink: "#3a2233", muted: "#9a6b86", accent: "#ff4fa3", accent2: "#7c5cff", like: "#16a34a", never: "#e5484d", later: "#f59e0b" },
    radius: 26,
    font: "Fredoka",
    images: { background: null, accent: null },
  },
];

/** The CSS custom-property name for each palette key (the runtime token map). */
export const PALETTE_TO_CSSVAR: Record<keyof ThemePalette, string> = {
  bg: "--bg",
  bg2: "--bg-2",
  card: "--card",
  ink: "--ink",
  muted: "--muted",
  accent: "--accent",
  accent2: "--accent-2",
  like: "--like",
  never: "--never",
  later: "--later",
};

export const MIN_MATCH_BUFFER = 3; // §1, §6.4: surface ≥3 mutual matches
